import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { isValidScriptPubKey } from "../identity";
import { optionalIntegerString, parseIntegerString } from "../units";
import { fetchJson } from "./client";
import { immutableCache } from "./cacheClasses";
import { queryKeys } from "./queryKeys";
import { isValidBlockHeight } from "./validation";

// explorer-api serializes with JsonIgnoreCondition.WhenWritingNull, so nullable
// fields are absent (not `null`) on the wire. Hydrators treat undefined as
// the absent-value sentinel — see CLAUDE.md "API client".

interface WireWalletMiningSeriesEntry {
  readonly height: string;
  readonly blockTime?: string;
  readonly txid: string;
  readonly txIndex: number;
  readonly miningDomainId: number;
  readonly gatesPass: boolean;
  readonly canonicalBlend?: string;
  readonly rewardRank?: number;
  readonly rewardCogtoshi: string;
  readonly feeSats?: string;
}

interface WireWalletMiningSeriesResponse {
  readonly scriptPubKey: string;
  readonly fromHeight?: string;
  readonly toHeight: string;
  readonly entries: readonly WireWalletMiningSeriesEntry[];
}

interface WireWalletSentencesEntry {
  readonly height: string;
  readonly blockTime?: string;
  readonly txid: string;
  readonly txIndex: number;
  readonly miningDomainId: number;
  readonly sentenceText?: string;
  readonly rawSentenceBytes: string;
  readonly gatesPass: boolean;
  readonly canonicalBlend?: string;
  readonly rewardRank?: number;
  readonly rewardCogtoshi: string;
}

interface WireWalletSentencesResponse {
  readonly scriptPubKey: string;
  readonly toHeight: string;
  readonly entries: readonly WireWalletSentencesEntry[];
  readonly nextCursor?: string;
}

interface WireWalletMiningEfficiencyResponse {
  readonly scriptPubKey: string;
  readonly fromHeight?: string;
  readonly toHeight: string;
  readonly submissions: number;
  readonly wins: number;
  readonly gateFailures: number;
  readonly cogtoshiWon: string;
  readonly satsSpent: string;
}

interface WireWalletOwnedDomainsEntry {
  readonly domainId: number;
  readonly name: string;
  readonly registeredAtHeight: string;
  readonly registeredAtBlockTime?: string;
}

interface WireWalletOwnedDomainsResponse {
  readonly scriptPubKey: string;
  readonly toHeight: string;
  readonly entries: readonly WireWalletOwnedDomainsEntry[];
}

interface WireWalletBalanceSeriesEntry {
  readonly height: string;
  readonly blockTime?: string;
  readonly oldBalance: string;
  readonly newBalance: string;
  readonly delta: string;
}

interface WireWalletBalanceSeriesResponse {
  readonly scriptPubKey: string;
  readonly fromHeight?: string;
  readonly toHeight: string;
  readonly entries: readonly WireWalletBalanceSeriesEntry[];
}

export interface WalletMiningSeriesEntry {
  readonly height: bigint;
  readonly blockTime: bigint | null;
  readonly txid: string;
  readonly txIndex: number;
  readonly miningDomainId: number;
  readonly gatesPass: boolean;
  readonly canonicalBlend: bigint | null;
  readonly rewardRank: number | null;
  readonly rewardCogtoshi: bigint;
  readonly feeSats: bigint | null;
}

export interface WalletMiningSeries {
  readonly scriptPubKey: string;
  readonly fromHeight: bigint | null;
  readonly toHeight: bigint;
  readonly entries: readonly WalletMiningSeriesEntry[];
}

export interface WalletSentenceEntry {
  readonly height: bigint;
  readonly blockTime: bigint | null;
  readonly txid: string;
  readonly txIndex: number;
  readonly miningDomainId: number;
  readonly sentenceText: string | null;
  readonly rawSentenceBytes: string;
  readonly gatesPass: boolean;
  readonly canonicalBlend: bigint | null;
  readonly rewardRank: number | null;
  readonly rewardCogtoshi: bigint;
}

export interface WalletSentencesPage {
  readonly scriptPubKey: string;
  readonly toHeight: bigint;
  readonly entries: readonly WalletSentenceEntry[];
  readonly nextCursor: string | null;
}

export interface WalletMiningEfficiency {
  readonly scriptPubKey: string;
  readonly fromHeight: bigint | null;
  readonly toHeight: bigint;
  readonly submissions: number;
  readonly wins: number;
  readonly gateFailures: number;
  readonly cogtoshiWon: bigint;
  readonly satsSpent: bigint;
}

export interface WalletOwnedDomain {
  readonly domainId: number;
  readonly name: string;
  readonly registeredAtHeight: bigint;
  readonly registeredAtBlockTime: bigint | null;
}

export interface WalletOwnedDomains {
  readonly scriptPubKey: string;
  readonly toHeight: bigint;
  readonly entries: readonly WalletOwnedDomain[];
}

export interface WalletBalanceSeriesEntry {
  readonly height: bigint;
  readonly blockTime: bigint | null;
  readonly oldBalance: bigint;
  readonly newBalance: bigint;
  readonly delta: bigint;
}

export interface WalletBalanceSeries {
  readonly scriptPubKey: string;
  readonly fromHeight: bigint | null;
  readonly toHeight: bigint;
  readonly entries: readonly WalletBalanceSeriesEntry[];
}

export function hydrateWalletMiningSeries(
  wire: WireWalletMiningSeriesResponse,
): WalletMiningSeries {
  return {
    scriptPubKey: wire.scriptPubKey,
    fromHeight: optionalIntegerString(wire.fromHeight),
    toHeight: parseIntegerString(wire.toHeight),
    entries: wire.entries.map((e) => ({
      height: parseIntegerString(e.height),
      blockTime: optionalIntegerString(e.blockTime),
      txid: e.txid,
      txIndex: e.txIndex,
      miningDomainId: e.miningDomainId,
      gatesPass: e.gatesPass,
      canonicalBlend: optionalIntegerString(e.canonicalBlend),
      rewardRank: e.rewardRank ?? null,
      rewardCogtoshi: parseIntegerString(e.rewardCogtoshi),
      feeSats: optionalIntegerString(e.feeSats),
    })),
  };
}

export function hydrateWalletSentencesPage(
  wire: WireWalletSentencesResponse,
): WalletSentencesPage {
  return {
    scriptPubKey: wire.scriptPubKey,
    toHeight: parseIntegerString(wire.toHeight),
    entries: wire.entries.map((e) => ({
      height: parseIntegerString(e.height),
      blockTime: optionalIntegerString(e.blockTime),
      txid: e.txid,
      txIndex: e.txIndex,
      miningDomainId: e.miningDomainId,
      sentenceText: e.sentenceText ?? null,
      rawSentenceBytes: e.rawSentenceBytes,
      gatesPass: e.gatesPass,
      canonicalBlend: optionalIntegerString(e.canonicalBlend),
      rewardRank: e.rewardRank ?? null,
      rewardCogtoshi: parseIntegerString(e.rewardCogtoshi),
    })),
    nextCursor: wire.nextCursor ?? null,
  };
}

export function hydrateWalletMiningEfficiency(
  wire: WireWalletMiningEfficiencyResponse,
): WalletMiningEfficiency {
  return {
    scriptPubKey: wire.scriptPubKey,
    fromHeight: optionalIntegerString(wire.fromHeight),
    toHeight: parseIntegerString(wire.toHeight),
    submissions: wire.submissions,
    wins: wire.wins,
    gateFailures: wire.gateFailures,
    cogtoshiWon: parseIntegerString(wire.cogtoshiWon),
    satsSpent: parseIntegerString(wire.satsSpent),
  };
}

export function hydrateWalletOwnedDomains(
  wire: WireWalletOwnedDomainsResponse,
): WalletOwnedDomains {
  return {
    scriptPubKey: wire.scriptPubKey,
    toHeight: parseIntegerString(wire.toHeight),
    entries: wire.entries.map((e) => ({
      domainId: e.domainId,
      name: e.name,
      registeredAtHeight: parseIntegerString(e.registeredAtHeight),
      registeredAtBlockTime: optionalIntegerString(e.registeredAtBlockTime),
    })),
  };
}

export function hydrateWalletBalanceSeries(
  wire: WireWalletBalanceSeriesResponse,
): WalletBalanceSeries {
  return {
    scriptPubKey: wire.scriptPubKey,
    fromHeight: optionalIntegerString(wire.fromHeight),
    toHeight: parseIntegerString(wire.toHeight),
    entries: wire.entries.map((e) => ({
      height: parseIntegerString(e.height),
      blockTime: optionalIntegerString(e.blockTime),
      oldBalance: parseIntegerString(e.oldBalance),
      newBalance: parseIntegerString(e.newBalance),
      delta: parseIntegerString(e.delta),
    })),
  };
}

export function useWalletMiningSeries(
  scriptPubKey: string | null,
  toHeight: number | null,
  fromHeight: number | null = null,
): UseQueryResult<WalletMiningSeries> {
  return useQuery({
    queryKey: queryKeys.wallet.miningSeries(scriptPubKey ?? "", toHeight ?? -1, fromHeight),
    queryFn: async () => {
      const base = `/api/v1/wallets/${scriptPubKey ?? ""}/mining/series/${(toHeight ?? 0).toString()}`;
      const path =
        fromHeight === null ? base : `${base}?fromHeight=${fromHeight.toString()}`;
      return hydrateWalletMiningSeries(
        await fetchJson<WireWalletMiningSeriesResponse>(path),
      );
    },
    enabled: isValidScriptPubKey(scriptPubKey) && isValidBlockHeight(toHeight),
    ...immutableCache,
  });
}

export interface WalletSentencesPageOptions {
  readonly cursor?: string | null;
  readonly limit?: number;
}

export function useWalletSentencesPage(
  scriptPubKey: string | null,
  toHeight: number | null,
  options: WalletSentencesPageOptions = {},
): UseQueryResult<WalletSentencesPage> {
  const cursor = options.cursor ?? null;
  const limit = options.limit;
  return useQuery({
    queryKey: queryKeys.wallet.sentencesPage(
      scriptPubKey ?? "",
      toHeight ?? -1,
      cursor,
      limit ?? null,
    ),
    queryFn: async () => {
      const base = `/api/v1/wallets/${scriptPubKey ?? ""}/sentences/${(toHeight ?? 0).toString()}`;
      const params = new URLSearchParams();
      if (cursor !== null && cursor !== "") params.set("cursor", cursor);
      if (limit !== undefined) params.set("limit", limit.toString());
      const qs = params.toString();
      const path = qs.length === 0 ? base : `${base}?${qs}`;
      return hydrateWalletSentencesPage(await fetchJson<WireWalletSentencesResponse>(path));
    },
    enabled: isValidScriptPubKey(scriptPubKey) && isValidBlockHeight(toHeight),
    ...immutableCache,
  });
}

export function useWalletMiningEfficiency(
  scriptPubKey: string | null,
  toHeight: number | null,
  fromHeight: number | null = null,
): UseQueryResult<WalletMiningEfficiency> {
  return useQuery({
    queryKey: queryKeys.wallet.miningEfficiency(
      scriptPubKey ?? "",
      toHeight ?? -1,
      fromHeight,
    ),
    queryFn: async () => {
      const base = `/api/v1/wallets/${scriptPubKey ?? ""}/mining/efficiency/${(toHeight ?? 0).toString()}`;
      const path =
        fromHeight === null ? base : `${base}?fromHeight=${fromHeight.toString()}`;
      return hydrateWalletMiningEfficiency(
        await fetchJson<WireWalletMiningEfficiencyResponse>(path),
      );
    },
    enabled: isValidScriptPubKey(scriptPubKey) && isValidBlockHeight(toHeight),
    ...immutableCache,
  });
}

export function useWalletOwnedDomains(
  scriptPubKey: string | null,
  toHeight: number | null,
): UseQueryResult<WalletOwnedDomains> {
  return useQuery({
    queryKey: queryKeys.wallet.ownedDomains(scriptPubKey ?? "", toHeight ?? -1),
    queryFn: async () => {
      const path = `/api/v1/wallets/${scriptPubKey ?? ""}/domains/${(toHeight ?? 0).toString()}`;
      return hydrateWalletOwnedDomains(
        await fetchJson<WireWalletOwnedDomainsResponse>(path),
      );
    },
    enabled: isValidScriptPubKey(scriptPubKey) && isValidBlockHeight(toHeight),
    ...immutableCache,
  });
}

export function useWalletBalanceSeries(
  scriptPubKey: string | null,
  toHeight: number | null,
  fromHeight: number | null = null,
): UseQueryResult<WalletBalanceSeries> {
  return useQuery({
    queryKey: queryKeys.wallet.balanceSeries(scriptPubKey ?? "", toHeight ?? -1, fromHeight),
    queryFn: async () => {
      const base = `/api/v1/wallets/${scriptPubKey ?? ""}/balance/series/${(toHeight ?? 0).toString()}`;
      const path =
        fromHeight === null ? base : `${base}?fromHeight=${fromHeight.toString()}`;
      return hydrateWalletBalanceSeries(
        await fetchJson<WireWalletBalanceSeriesResponse>(path),
      );
    },
    enabled: isValidScriptPubKey(scriptPubKey) && isValidBlockHeight(toHeight),
    ...immutableCache,
  });
}
